/**
 * A weekly room's Sessions in Season: the schedule, the sessions coming up (a tap opens what each can do), the past
 * ones with their reports, and who keeps coming across the series, which the iPad on its side shows in its right pane.
 */
import { type ReactNode, useState } from "react";
import { useNavigate } from "react-router";
import type { RoomAudienceResponse, WebinarDetail, WebinarLive } from "../../../shared/types";
import { daysUntil, labelParts } from "../../../shared/live-time";
import { timeOf } from "../../board/series";
import { Fold } from "../../components/season/Fold";
import { GroupedList, ListRow } from "../../components/season/GroupedList";
import type { SeasonLayout } from "../../components/season/layout";
import { GroupHead } from "../../components/season/PageHead";
import { SeasonCard } from "../../components/season/Card";
import { Button } from "../../components/ui/Button";
import { FaceStack } from "../../components/ui/Face";
import { Icon } from "../../components/ui/Icon";
import { Sheet } from "../../components/ui/Sheet";
import { longDay, sessionReportPath } from "./liveWords";
import { GROUP_WORDS, sentLine } from "./roomWords";
import { acrossGroup, PAST_SHOWN, SessionDialogs, type useSessions } from "./sessionsParts";

type SessionsState = ReturnType<typeof useSessions>;
type SessionView = SessionsState["upcoming"][number];

const n = (v: number) => v.toLocaleString("en-US");

function DateBox({ iso, tz, on = false }: { iso: string; tz: string; on?: boolean }) {
  const p = labelParts(iso, tz, "short");
  return (
    <span className={on ? "rm-db on" : "rm-db"}>
      <span>{p?.month ?? ""}</span>
      <b>{p?.day ?? ""}</b>
    </span>
  );
}

/** What a coming session can do: its topic, move, skip, bring back. Opens from its row. */
function SessionActions({ s, state, onClose }: { s: SessionView; state: SessionsState; onClose: () => void }) {
  const items = state.menuFor(s);
  const topic = s.state !== "skipped";
  return (
    <Sheet open onClose={onClose} title={`${longDay(s.startsAt, s.timezone)}, ${timeOf(s.startsAt, s.timezone)}`}>
      <GroupedList>
        {topic ? (
          <ListRow
            icon="pencil"
            title={s.topic ? "Change its topic" : "Set its topic"}
            onClick={() => {
              onClose();
              state.setPop({ kind: "topic", session: s });
            }}
          />
        ) : null}
        {items.map((i) => (
          <ListRow
            key={i.label}
            icon={i.icon ?? "list"}
            title={i.label}
            {...(i.danger || i.icon === "x" ? { tone: "danger" as const } : {})}
            onClick={() => {
              onClose();
              i.onSelect();
            }}
          />
        ))}
      </GroupedList>
    </Sheet>
  );
}

/** Regulars, who dropped off, and who is new since the first session. */
export function AcrossSeries({
  detail,
  live,
  across,
  state,
  layout,
  hostFirst,
}: {
  detail: WebinarDetail;
  live: WebinarLive;
  across: RoomAudienceResponse | null;
  state: SessionsState;
  layout: SeasonLayout;
  hostFirst: string;
}) {
  const series = live.series;
  if (!series || state.ran === 0) return null;
  const faces = (key: "regulars" | "dropped" | "joined_late") => (acrossGroup(across, key)?.faces ?? []).map((f) => ({ name: f.name, photo: f.avatar_url }));
  const dropped = acrossGroup(across, "dropped");
  const sendable = !!dropped && dropped.reach > 0;
  const rows: Array<{ key: string; count: number; label: string; sub: ReactNode; faces: Array<{ name: string; photo: string | null }>; onClick?: () => void }> = [
    { key: "regulars", count: series.results.regulars, label: "Regulars", sub: "Came to every one", faces: faces("regulars") },
    {
      key: "dropped",
      count: series.results.droppedOff,
      label: "Dropped off",
      sub: (
        <>
          <span>Missed the last two</span>
          {dropped?.sent ? (
            <span className="rm-sent">
              <Icon name="check" /> {sentLine(dropped.sent, Date.now())}
            </span>
          ) : null}
          {sendable ? (
            <span className="k-clamp">{dropped.sent ? GROUP_WORDS.dropped.again(dropped.reach) : GROUP_WORDS.dropped.act(dropped.reach)}</span>
          ) : null}
        </>
      ),
      faces: faces("dropped"),
      ...(sendable ? { onClick: () => state.sendDropped(dropped, hostFirst) } : {}),
    },
    { key: "joined", count: series.results.joinedLate, label: "New since the first", sub: undefined, faces: faces("joined_late") },
  ];
  const first = state.firstRan;
  return (
    <>
      <GroupHead title="Across the series" count={first ? `Since ${longDay(first.startsAt, first.timezone).split(" ").slice(1).join(" ")}` : undefined} />
      <GroupedList className="rm-across" label={`Across the series, ${detail.title}`}>
        {rows.map((r) => (
          <ListRow
            key={r.key}
            lead={<span className="rm-n">{n(r.count)}</span>}
            title={r.label}
            sub={r.sub}
            end={layout !== "phone" && r.faces.length ? <FaceStack people={r.faces} size={28} /> : undefined}
            {...(r.onClick ? { onClick: r.onClick } : {})}
          />
        ))}
      </GroupedList>
    </>
  );
}

export function SessionsSection({
  detail,
  live,
  across,
  state,
  layout,
  hostFirst,
  onSent,
  onSaved,
}: {
  detail: WebinarDetail;
  live: WebinarLive;
  across: RoomAudienceResponse | null;
  state: SessionsState;
  layout: SeasonLayout;
  hostFirst: string;
  onSent: () => void;
  onSaved: Parameters<typeof SessionDialogs>[0]["onSaved"];
}) {
  const navigate = useNavigate();
  const [acting, setActing] = useState<SessionView | null>(null);
  const [allPast, setAllPast] = useState(false);
  const { series, tz, now, upcoming, past, ran, first, lengthMin, firstRan } = state;
  if (!series) return null;
  const shownPast = allPast ? past : past.slice(0, PAST_SHOWN);
  const schedule = (
    <SeasonCard className="rm-sched">
      <div>
        <h2 className="k-card-h rm-sh">Schedule</h2>
        <p className="k-rm">
          {series.long}, {lengthMin} min · {n(ran)} so far{series.ended ? " · ended" : ""}
        </p>
      </div>
      {series.ended ? null : (
        <Button icon="cal" onClick={() => state.setPop({ kind: "change" })}>
          Change the time
        </Button>
      )}
    </SeasonCard>
  );
  const coming = upcoming.length ? (
    <>
      <GroupHead title="Coming up" />
      <GroupedList className="rm-dl">
        {upcoming.map((s) => {
          const skipped = s.state === "skipped";
          const days = daysUntil(s.startsAt, s.timezone, now);
          const soon = s === first ? (s.state === "live" ? "Live now" : days === 0 ? "Today" : days === 1 ? "Tomorrow" : "") : "";
          const canAct = skipped || s.state === "scheduled" || s.state === "live";
          return (
            <ListRow
              key={s.id}
              lead={<DateBox iso={s.startsAt} tz={s.timezone} on={s === first} />}
              title={`${soon ? `${soon}, ` : `${longDay(s.startsAt, s.timezone).split(" ")[0]}, `}${timeOf(s.startsAt, s.timezone)}`}
              sub={
                skipped ? (
                  "Skipped. Everyone with a seat was told."
                ) : s.topic ? (
                  `Topic: ${s.topic}${s.movedFrom ? ` · moved from ${longDay(s.movedFrom, tz)}` : ""}`
                ) : (
                  <em className="k-hot">Set its topic</em>
                )
              }
              value={skipped ? undefined : `${n(s.registered)} registered`}
              {...(canAct ? { onClick: () => setActing(s) } : {})}
            />
          );
        })}
      </GroupedList>
    </>
  ) : null;
  const pastRows = (
    <>
      <GroupHead title="Past sessions" />
      {past.length === 0 ? (
        <p className="k-rm">After each session it shows here, with its report.</p>
      ) : (
        <GroupedList className="rm-dl">
          {shownPast.map((s) => {
            const pct = s.registered ? Math.round((s.cameLive / s.registered) * 100) : 0;
            const ranIt = s.state === "ended";
            return (
              <ListRow
                key={s.id}
                lead={<DateBox iso={s.startsAt} tz={s.timezone} />}
                title={longDay(s.startsAt, s.timezone)}
                sub={ranIt ? `${n(s.cameLive)} of ${n(s.registered)} came · ${pct}%` : s.state === "missed" ? "Didn't happen" : "Skipped"}
                {...(ranIt ? { onClick: () => navigate(sessionReportPath(detail.id, s.id)) } : {})}
              />
            );
          })}
        </GroupedList>
      )}
      {past.length > PAST_SHOWN && !allPast ? (
        <Fold
          title={`Show ${past.length - PAST_SHOWN} more`}
          line={firstRan ? `And earlier, from ${longDay(firstRan.startsAt, firstRan.timezone)}` : undefined}
          isOpen={false}
          onClick={() => setAllPast(true)}
        />
      ) : null}
    </>
  );
  const acrossBlock = <AcrossSeries detail={detail} live={live} across={across} state={state} layout={layout} hostFirst={hostFirst} />;
  return (
    <>
      {schedule}
      {layout === "upright" ? (
        <div className="k-cols rm-cols">
          <div>
            {coming}
            {acrossBlock}
          </div>
          <div>{pastRows}</div>
        </div>
      ) : (
        <>
          {coming}
          {pastRows}
          {layout === "side" ? null : acrossBlock}
        </>
      )}
      {acting ? <SessionActions s={acting} state={state} onClose={() => setActing(null)} /> : null}
      <SessionDialogs detail={detail} live={live} state={state} onSaved={onSaved} onSent={onSent} />
    </>
  );
}
